import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Platform } from 'react-native';

import { providerKeys } from '@/hooks/use-provider';
import { teamKeys } from '@/hooks/use-team';
import { TeamPricing, TeamSelection } from '@/lib/team-pricing';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';

export const teamPricingKeys = {
  pricing: () => ['stripe-plans', 'team'] as const,
};

/** Versão ativa da fórmula + escada Apple. Público: a landing também usa. */
export function useTeamPricing(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teamPricingKeys.pricing(),
    queryFn: async () => (await api.get<TeamPricing>('/stripe/plans/team')).data,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  });
}

/**
 * O plano Equipe pode ser contratado nesta plataforma? No iPhone só com a
 * escada Apple configurada: sem ela a única saída seria fechar por WhatsApp,
 * o que a App Store trata como compra fora do IAP (3.1.1).
 */
export function useTeamPlanAvailable(options?: { enabled?: boolean }) {
  const isIOS = Platform.OS === 'ios';
  const { data } = useTeamPricing({ enabled: isIOS && options?.enabled !== false });
  // Android: o plano de equipe ainda não é vendido pelo Google Play.
  if (Platform.OS === 'android') return false;
  if (!isIOS) return true;
  return (data?.appleLadder.length ?? 0) > 0;
}

/** Contratado muda: organização, perfil (assinatura) e o medidor de uso. */
function useInvalidateBilling() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: teamKeys.all }),
      queryClient.invalidateQueries({ queryKey: providerKeys.all }),
    ]);
    await Promise.resolve(useAuthStore.getState().fetchProfile());
  };
}

/**
 * Assinatura de equipe já no Stripe: troca só as quantidades (o Stripe faz o
 * rateio). A API recusa menos profissionais do que os ativos que atendem.
 */
export function useUpdateTeamSubscription() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (selection: TeamSelection) =>
      (await api.patch('/stripe/team/subscription', selection)).data,
    onSuccess: invalidate,
  });
}

/**
 * iPhone: dentro do degrau já comprado, o dono troca profissionais por
 * clientes sem nova compra. A API confere `fórmula ≤ preço do degrau`.
 */
export function useRedistributeAppleTeamPlan() {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: async (selection: TeamSelection) =>
      (await api.post('/apple-iap/team/redistribute', selection)).data,
    onSuccess: invalidate,
  });
}
